import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import type { ThumbnailStyle, ThumbnailStyleId } from '@/@types/aiStyle';
import { DEFAULT_SELECTED_ID } from '@/constants/aiStyleThumbnails';
import { aiStyleManager } from '@/utils/aiStyleUtils';
import Thumbnail from './Tumbnail';

interface CreateAiImageModalTemplateProps {
  selectedStyleId?: ThumbnailStyleId | null;
  imageDescription?: string;
  onStyleSelect?: (styleId: ThumbnailStyleId) => void;
  onDescriptionChange?: (description: string) => void;
  onStyleDataChange?: (styleData: ThumbnailStyle | null) => void;
}


const MAX_DESCRIPTION_LENGTH = 200;

function CreateAiImageModalTemplate({
  selectedStyleId: externalSelectedStyleId,
  imageDescription: externalImageDescription,
  onStyleSelect,
  onDescriptionChange,
  onStyleDataChange
}: CreateAiImageModalTemplateProps) {
  // 메모이제이션된 썸네일 데이터
  const thumbnails = useMemo(() => aiStyleManager.getAllStyles(), []);

  // 기본값으로 DEFAULT_SELECTED_ID 사용
  const [internalSelectedStyleId, setInternalSelectedStyleId] = useState<ThumbnailStyleId | null>(
    DEFAULT_SELECTED_ID
  );
  const [internalImageDescription, setInternalImageDescription] = useState<string>('');

  // 외부 props와 내부 상태 동기화
  useEffect(() => {
    if (externalSelectedStyleId !== undefined) {
      setInternalSelectedStyleId(externalSelectedStyleId);
      const selectedThumbnail = aiStyleManager.getStyleById(externalSelectedStyleId);
      onStyleDataChange?.(selectedThumbnail);
    }
  }, [externalSelectedStyleId, onStyleDataChange]);

  useEffect(() => {
    if (externalImageDescription !== undefined) {
      setInternalImageDescription(externalImageDescription);
    }
  }, [externalImageDescription]);

  // 컴포넌트 초기 로드 시 기본 썸네일 데이터를 부모에게 전달
  useEffect(() => {
    if (externalSelectedStyleId === undefined && internalSelectedStyleId) {
      const selectedThumbnail = aiStyleManager.getStyleById(internalSelectedStyleId);
      onStyleDataChange?.(selectedThumbnail);
      onStyleSelect?.(internalSelectedStyleId);
    }
  }, []);

  // 현재 사용할 값 결정
  const currentSelectedStyleId = externalSelectedStyleId ?? internalSelectedStyleId;
  const currentImageDescription = externalImageDescription ?? internalImageDescription;

  // 스타일 선택 핸들러
  const handleStyleSelect = useCallback(
    (styleId: ThumbnailStyleId) => {
      const selectedThumbnail = aiStyleManager.getStyleById(styleId);

      if (externalSelectedStyleId !== undefined) {
        onStyleSelect?.(styleId);
        onStyleDataChange?.(selectedThumbnail);
      } else {
        setInternalSelectedStyleId(styleId);
        onStyleSelect?.(styleId);
        onStyleDataChange?.(selectedThumbnail);
      }
    },
    [externalSelectedStyleId, onStyleSelect, onStyleDataChange]
  );

  // 설명 변경 핸들러
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;

      // MAX_DESCRIPTION_LENGTH를 초과하는 입력 방지
      if (value.length > MAX_DESCRIPTION_LENGTH) {
        return;
      }

      if (externalImageDescription !== undefined) {
        onDescriptionChange?.(value);
      } else {
        setInternalImageDescription(value);
        onDescriptionChange?.(value);
      }
    },
    [externalImageDescription, onDescriptionChange]
  );

  return (
    <div css={styles.container}>
      {/* 이미지 스타일 영역 */}
      <section css={styles.section}>
        <header css={styles.header}>
          <h3 css={styles.title}>이미지 스타일</h3>
          <p css={styles.description}>선택한 스타일로 일관되게 이미지를 만들어요.</p>
        </header>

        <div css={styles.thumbnailGrid}>
          {thumbnails.map((thumb) => (
            <Thumbnail
              key={thumb.id}
              title={thumb.title}
              image={thumb.image}
              isSelected={currentSelectedStyleId === thumb.id}
              onClick={() => handleStyleSelect(thumb.id)}
            />
          ))}
        </div>
      </section>

      {/* 이미지 설명 영역 */}
      <section css={styles.section}>
        <header css={styles.header}>
          <h3 css={styles.title}>어떤 이미지를 만들까요?</h3>
        </header>

        <div css={styles.textAreaContainer}>
          <textarea
            css={styles.textArea}
            value={currentImageDescription}
            onChange={handleDescriptionChange}
            placeholder="이미지에 담겼으면 하는 분위기와 상황을 간단히 적어주세요. (예: 긴장되는 추격전, 잔잔한 숲속 모험)"
            maxLength={MAX_DESCRIPTION_LENGTH}
          />
          <div css={styles.characterCount}>
            {currentImageDescription.length}/{MAX_DESCRIPTION_LENGTH}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CreateAiImageModalTemplate;

// 스타일 정의
const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
  `,

  section: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,

  header: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
  `,

  title: css`
    font-family: 'Pretendard', sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: #0e1017;
    margin: 0;
  `,

  description: css`
    font-family: 'Pretendard', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #626262;
    margin: 0;
  `,

  thumbnailGrid: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  `,

  textAreaContainer: css`
    position: relative;
    width: 100%;
    background-color: #fafafa;
    border: 1px solid #ededed;
    border-radius: 8px;
    overflow: hidden;
  `,

  textArea: css`
    width: 100%;
    height: 80px;
    padding: 12px 12px 28px 12px;
    border: none;
    background: transparent;
    font-family: 'Pretendard', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #0e1017;
    resize: none;
    outline: none;

    &::placeholder {
      color: #b2b6c2;
    }

    &:focus {
      outline: none;
    }
  `,

  characterCount: css`
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-family: 'Pretendard', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #626262;
    background-color: #fafafa;
    pointer-events: none;
  `
};
